import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Sparkles, Send, Loader2 } from "lucide-react";
import { apiGet, apiPost } from "@/lib/api";

const QA_EVENT = "open-qa-bot";
export const openQABot = () => window.dispatchEvent(new CustomEvent(QA_EVENT));

/**
 * QABot — floating sky-blue chat bubble that opens an AI-powered FAQ panel.
 * Curated presets show first; if the user types a custom question it falls
 * back to the AI via /api/faq/ask.
 */
export default function QABot() {
  const [open, setOpen] = useState(false);
  const [presets, setPresets] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(QA_EVENT, handler);
    return () => window.removeEventListener(QA_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!open || presets.length) return;
    apiGet("/faq/presets")
      .then((d) => setPresets(d.presets || []))
      .catch(() => setPresets([]));
  }, [open, presets.length]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const ask = async (question, presetAnswer) => {
    if (!question.trim()) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", body: question }]);
    if (presetAnswer) {
      setMessages((m) => [...m, { role: "bot", body: presetAnswer, source: "preset" }]);
      return;
    }
    setLoading(true);
    try {
      const res = await apiPost("/faq/ask", { question });
      setMessages((m) => [
        ...m,
        { role: "bot", body: res.answer || "Sorry, I couldn't fetch an answer.", source: res.source },
      ]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "bot",
          body: "Hmm, I'm having trouble right now. Try the AI Quote button or call us — number in the footer.",
          source: "error",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <motion.button
        data-testid="qa-bot-trigger"
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.7, duration: 0.6 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Ask a question"
        className="float-cta fixed bottom-6 left-6 z-[60] flex items-center gap-2 px-4 py-3 rounded-full bg-white/5 hover:bg-white/10 backdrop-blur border border-sky-500/40 text-white text-sm shadow-[0_10px_40px_rgba(14,165,233,0.25)]"
      >
        <MessageCircle className="w-4 h-4 text-sky-300" />
        Have a question?
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[150] flex items-end sm:items-center justify-center p-0 sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ y: 40, opacity: 0, scale: 0.97 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 40, opacity: 0, scale: 0.97 }}
              transition={{ type: "spring", damping: 25, stiffness: 220 }}
              className="relative w-full sm:max-w-lg h-[85vh] sm:h-[640px] sm:max-h-[90vh] flex flex-col rounded-t-2xl sm:rounded-2xl border border-sky-500/20 bg-[#0d0d0d] shadow-2xl overflow-hidden"
              data-testid="qa-bot-panel"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 bg-gradient-to-r from-sky-500/10 to-transparent">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-sky-500/20 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-sky-300" />
                  </div>
                  <div>
                    <p className="font-['Outfit'] text-white text-base">Ask Reflections AI</p>
                    <p className="text-[11px] text-sky-300/80">Detailing questions, pricing, packages</p>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="text-gray-400 hover:text-white p-2 -m-2"
                  data-testid="qa-bot-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3">
                {messages.length === 0 && (
                  <>
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                      <p className="text-sm text-gray-300 leading-relaxed">
                        👋 Hey! I'm the Reflections AI assistant. Tap a question below, or ask anything in your own words.
                      </p>
                    </div>
                    <div className="space-y-2">
                      {presets.map((p) => (
                        <button
                          key={p.q}
                          onClick={() => ask(p.q, p.a)}
                          className="w-full text-left rounded-lg border border-white/5 bg-white/[0.02] hover:bg-sky-500/10 hover:border-sky-500/30 transition-colors px-4 py-3 text-sm text-gray-200"
                          data-testid={`qa-preset-${p.q.slice(0, 15)}`}
                        >
                          {p.q}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {messages.map((m, i) => (
                  <div
                    key={`${m.role}-${i}`}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                        m.role === "user"
                          ? "bg-sky-500 text-white rounded-tr-sm"
                          : "bg-white/[0.04] text-gray-200 border border-white/10 rounded-tl-sm"
                      }`}
                    >
                      {m.body}
                      {m.source === "ai" && (
                        <span className="block text-[10px] text-sky-400/60 mt-1 uppercase tracking-wider">
                          AI-generated
                        </span>
                      )}
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3">
                      <Loader2 className="w-4 h-4 animate-spin text-sky-400" />
                    </div>
                  </div>
                )}
              </div>

              {/* Input */}
              <form
                onSubmit={(e) => { e.preventDefault(); ask(input); }}
                className="flex items-center gap-2 p-3 border-t border-white/5 bg-black/40"
              >
                <input
                  data-testid="qa-bot-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your question…"
                  className="flex-1 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-sky-500/50"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  data-testid="qa-bot-send"
                  className="w-10 h-10 rounded-full bg-sky-500 hover:bg-sky-400 disabled:opacity-40 flex items-center justify-center transition-colors"
                  aria-label="Send"
                >
                  <Send className="w-4 h-4 text-white" />
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
